import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { contracts } from "../config";
import {
  fromUnits, getBook, getExchangeConfig, listMarkets, symCode, symPrec,
  type ExchangeConfig, type Market,
} from "../api/exchangeApi";
import { IpfsImage } from "../components/IpfsImage";

interface Row { m: Market; bid?: string; ask?: string }

export function Exchange() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [cfg, setCfg] = useState<ExchangeConfig | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, markets] = await Promise.all([getExchangeConfig(), listMarkets()]);
        setCfg(c);
        const out = await Promise.all(
          markets.filter((m) => m.enabled).map(async (m) => {
            const [b, a] = await Promise.all([getBook(m.id, "bids", 1), getBook(m.id, "asks", 1)]);
            return { m, bid: b[0]?.price, ask: a[0]?.price };
          })
        );
        setRows(out);
      } catch {
        setErr(`The exchange contract (${contracts.orderbook}) isn't reachable or isn't installed yet.`);
        setRows([]);
      }
    })();
  }, []);

  return (
    <div className="page">
      <div className="page__header">
        <h1>Token Exchange</h1>
        <p className="page__subtitle">
          Limit-order books for game tokens paired with WAX. Orders live on chain in the{" "}
          <code>{contracts.orderbook}</code> contract; this site never holds your tokens.
          {cfg && <> Taker fee: {(cfg.taker_fee_bps / 100).toFixed(2)}%. Makers pay nothing.</>}
        </p>
      </div>
      {cfg?.paused && <div className="callout callout--warning">Trading is paused right now. You can still cancel open orders.</div>}
      {err && <div className="state-message state-message--error">{err}</div>}
      {rows === null && <div className="state-message">Loading markets…</div>}
      {rows && rows.length === 0 && !err && <div className="state-message">No markets listed yet.</div>}
      {rows && rows.length > 0 && (
        <div className="ex-market-grid">
          {rows.map(({ m, bid, ask }) => {
            const qp = symPrec(m.quote), q = symCode(m.quote);
            return (
              <Link key={m.id} to={`/exchange/${m.id}`} className="ex-market-card">
                <div className="ex-market-card__head">
                  {m.logo ? <IpfsImage hash={m.logo} alt="" className="ex-market-card__logo" placeholderClassName="ex-market-card__logo ex-market-card__logo--blank" /> : <div className="ex-market-card__logo ex-market-card__logo--blank">{symCode(m.base).slice(0, 2)}</div>}
                  <div>
                    <div className="ex-market-card__title">{m.title || `${symCode(m.base)} / ${q}`}</div>
                    <div className="ex-market-card__sub">{symCode(m.base)}@{m.base.contract}</div>
                  </div>
                </div>
                <dl className="ex-market-card__stats">
                  <div><dt>Last</dt><dd>{BigInt(m.last_price) > 0n ? `${fromUnits(m.last_price, qp)} ${q}` : "—"}</dd></div>
                  <div><dt>Bid</dt><dd className="ex-up">{bid ? fromUnits(bid, qp) : "—"}</dd></div>
                  <div><dt>Ask</dt><dd className="ex-down">{ask ? fromUnits(ask, qp) : "—"}</dd></div>
                  <div><dt>Volume</dt><dd>{Number(fromUnits(m.volume_quote, qp)).toLocaleString(undefined, { maximumFractionDigits: 2 })} {q}</dd></div>
                </dl>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
